/**
 * US 13.6 — "Find tasks": Mochi reads a conversation the person can already
 * see (an email thread, chat messages) and suggests tasks, each with its
 * owner, due date and the sentence it came from. Suggestions are edited,
 * unticked or accepted together on the same preview card; nothing exists
 * until accepted, and accepted tasks link back to the conversation.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoaderCircle, Sparkles } from "lucide-react";
import { Modal } from "../hubs/parts";
import { readLastSquad } from "../capture/api";
import { mochiApi, type Proposal } from "./api";
import { ProposalCard } from "./ProposalCard";

export interface ConversationLine {
  ref: string;
  author?: string;
  text: string;
}

type Found = Proposal | { card: null; message: string; items: [] };

/** Reads either the lines given (an email, a chat) or, for a meeting, its
 *  transcript on the server (`load`). */
export function FindTasksDialog({ lines = [], source = {}, load, onClose }: { lines?: ConversationLine[]; source?: Record<string, unknown>; load?: () => Promise<Found>; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "ready"; proposal: Proposal } | { kind: "none"; message: string } | { kind: "error"; message: string }>({ kind: "loading" });
  useEffect(() => {
    let alive = true;
    (load ? load() : mochiApi.suggest({ messages: lines.filter((l) => l.text.trim()).slice(-200), source, squad: readLastSquad()?.squad ?? null }))
      .then(
        (r) => alive && setState(r.card ? { kind: "ready", proposal: r as Proposal } : { kind: "none", message: (r as { message: string }).message }),
        (err) => alive && setState({ kind: "error", message: (err as Error).message }),
      );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Modal onClose={onClose} labelledBy="find-tasks-title" className="max-w-2xl" z="z-[90]">
      <div className="p-6 space-y-4 overflow-y-auto max-h-[85vh]" dir={i18n.dir()}>
        <h2 id="find-tasks-title" className="text-lg font-bold flex items-center gap-2">
          <Sparkles className="size-5 text-primary" /> {t("tasks.findTasks.title", { defaultValue: "Tasks Mochi found" })}
        </h2>
        {state.kind === "loading" && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <LoaderCircle className="size-4 animate-spin" /> {t("tasks.findTasks.reading", { defaultValue: "Reading the conversation…" })}
          </p>
        )}
        {(state.kind === "none" || state.kind === "error") && <p className="text-sm text-muted-foreground">{state.message}</p>}
        {state.kind === "ready" && <ProposalCard proposal={state.proposal} />}
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl text-sm font-semibold hover:bg-muted">
            {t("tasks.hubs.close", { defaultValue: "Close" })}
          </button>
        </div>
      </div>
    </Modal>
  );
}
